import React, {
  ForwardedRef,
  HTMLAttributes,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMultiSelect } from './useMultiSelect';
import {
  ButtonContext,
  ContextValue,
  LabelContext,
  ListBoxContext,
  ListStateContext,
  OverlayTriggerStateContext,
  PopoverContext,
  Provider,
  SelectRenderProps,
  SlotProps,
  TextContext,
  useContextProps,
  useSlottedContext,
} from 'react-aria-components';
import { filterDOMProps, useResizeObserver } from '@react-aria/utils';
import {
  AriaMultiSelectProps,
  MultiSelectState,
  RACValidation,
  RenderProps,
} from './MultiSelect.types';
import { useFocusRing } from 'react-aria';
import {
  FieldErrorContext,
  Hidden,
  // Hidden,
  forwardRefType,
  removeDataAttributes,
  useRenderProps,
  useSlot,
} from './MultiSelect.utils';
import { useMultiSelectState } from './useMultiSelectState';
import { CollectionDocumentContext, useCollectionDocument } from './Collection';

export interface PrimitiveMultiSelectProps<T extends object>
  extends Omit<
      AriaMultiSelectProps<T>,
      | 'children'
      | 'label'
      | 'description'
      | 'errorMessage'
      | 'validationState'
      | 'validationBehavior'
      // | 'items'
    >,
    RACValidation,
    RenderProps<SelectRenderProps>,
    SlotProps {}

export const MultiSelectContext =
  createContext<ContextValue<PrimitiveMultiSelectProps<any>, HTMLDivElement>>(null);
export const MultiSelectStateContext = createContext<MultiSelectState<unknown> | null>(null);

function PrimitiveMultiSelect<T extends object>(
  propsParam: PrimitiveMultiSelectProps<T>,
  refParam: ForwardedRef<HTMLDivElement>,
) {
  const [props, ref] = useContextProps(propsParam, refParam, MultiSelectContext);
  const { collection, document } = useCollectionDocument();
  const state = useMultiSelectState({
    ...props,
    collection,
    children: undefined,
    validationBehavior: props.validationBehavior ?? 'native',
  });

  const { isFocusVisible, focusProps } = useFocusRing({ within: true });

  // Get props for child elements from useSelect
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [labelRef, label] = useSlot();

  const {
    labelProps,
    triggerProps,
    valueProps,
    menuProps,
    descriptionProps,
    errorMessageProps,
    ...validation
  } = useMultiSelect(
    {
      ...removeDataAttributes(props),
      label,
      validationBehavior: props.validationBehavior ?? 'native',
    },
    state,
    buttonRef,
  );

  // Make menu width match input + button
  const [buttonWidth, setButtonWidth] = useState<string | null>(null);
  const onResize = useCallback(() => {
    if (buttonRef.current) {
      setButtonWidth(`${buttonRef.current.offsetWidth}px`);
    }
  }, [buttonRef]);

  useResizeObserver({
    ref: buttonRef,
    onResize,
  });

  // Only expose a subset of state to renderProps function to avoid infinite render loop
  const renderPropsState = useMemo(
    () => ({
      isOpen: state.isOpen,
      isFocused: state.isFocused,
      isFocusVisible,
      isDisabled: props.isDisabled || false,
      isInvalid: validation.isInvalid || false,
      isRequired: props.isRequired || false,
    }),
    [
      state.isOpen,
      state.isFocused,
      isFocusVisible,
      props.isDisabled,
      validation.isInvalid,
      props.isRequired,
    ],
  );

  const renderProps = useRenderProps({
    ...props,
    values: renderPropsState,
    defaultClassName: 'react-aria-MultiSelect',
  });

  const DOMProps = filterDOMProps(props);
  delete DOMProps.id;
  return (
    <>
      <Hidden>
        <Provider
          values={[
            [MultiSelectContext, props],
            [MultiSelectStateContext, state],
            [CollectionDocumentContext, document],
          ]}>
          {renderProps.children}
        </Provider>
      </Hidden>
      <Provider
        values={[
          [MultiSelectContext, props],
          [MultiSelectStateContext, state],
          [MultiSelectValueContext, valueProps],
          [LabelContext, { ...labelProps, ref: labelRef, elementType: 'span' }],
          [ButtonContext, { ...triggerProps, ref: buttonRef, isPressed: state.isOpen }],
          [OverlayTriggerStateContext, state],
          [
            PopoverContext,
            {
              trigger: 'Select',
              triggerRef: buttonRef,
              placement: 'bottom start',
              style: { '--trigger-width': buttonWidth } as React.CSSProperties,
            },
          ],
          [ListBoxContext, menuProps],
          [ListStateContext, state],
          [
            TextContext,
            {
              slots: {
                description: descriptionProps,
                errorMessage: errorMessageProps,
              },
            },
          ],
          [FieldErrorContext, validation],
        ]}>
        <div
          {...DOMProps}
          {...renderProps}
          {...focusProps}
          ref={ref}
          slot={props.slot || undefined}
          data-focused={state.isFocused || undefined}
          data-focus-visible={isFocusVisible || undefined}
          data-open={state.isOpen || undefined}
          data-disabled={props.isDisabled || undefined}
          data-invalid={validation.isInvalid || undefined}
          data-required={props.isRequired || undefined}
        />
      </Provider>
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
const _PrimitiveMultiSelect = (forwardRef as forwardRefType)(PrimitiveMultiSelect);
export { _PrimitiveMultiSelect as PrimitiveMultiSelect };

export interface MultiSelectValueRenderProps<T> {
  /**
   * Whether the value is a placeholder.
   * @selector [data-placeholder]
   */
  isPlaceholder: boolean;
  /** The object value of the currently selected item. */
  selectedItems: T[] | null;
  /** The textValue of the currently selected item. */
  selectedText: string | null;
}

export interface MultiSelectValueProps<T extends object>
  extends Omit<HTMLAttributes<HTMLElement>, keyof RenderProps<unknown>>,
    RenderProps<MultiSelectValueRenderProps<T>> {}

export const MultiSelectValueContext =
  createContext<ContextValue<MultiSelectValueProps<any>, HTMLSpanElement>>(null);

function MultiSelectValue<T extends object>(
  propsParam: MultiSelectValueProps<T>,
  refParam: ForwardedRef<HTMLSpanElement>,
) {
  const [props, ref] = useContextProps(propsParam, refParam, MultiSelectValueContext);
  const state = useContext(MultiSelectStateContext)!;
  const { placeholder } = useSlottedContext(MultiSelectContext)!;
  const selectedItems =
    state.selectionManager.selectedKeys.size > 0
      ? (Array.from(state.selectionManager.selectedKeys).map(
          (key) => state.collection.getItem(key)?.value,
        ) as T[])
      : null;

  const rendered =
    selectedItems && selectedItems?.length > 0 ? `Selected ${selectedItems?.length} items` : false;

  const renderProps = useRenderProps({
    ...props,
    defaultChildren: rendered || placeholder || 'Please select one or more items',
    defaultClassName: 'react-aria-SelectValue',
    values: {
      selectedItems,
      selectedText: 'Selected...',
      isPlaceholder: !selectedItems || selectedItems.length === 0,
    },
  });

  const DOMProps = filterDOMProps(props);

  return (
    <span ref={ref} {...DOMProps} {...renderProps} data-placeholder={!selectedItems || undefined}>
      {/* clear description and error message slots */}
      <TextContext.Provider value={undefined}>{renderProps.children}</TextContext.Provider>
    </span>
  );
}

// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
const _MultiSelectValue = (forwardRef as forwardRefType)(MultiSelectValue);
export { _MultiSelectValue as MultiSelectValue };
